/* eslint-disable @typescript-eslint/no-inferrable-types */
import { Type } from 'class-transformer';
import { SchoolType } from 'src/modules/enaio-certificates/shared/school-type.enum';
import { CustomInitializer } from 'src/modules/sm-base/shared/custom-initializer.interface';
import { SmBaseEntity } from 'src/modules/sm-base/shared/sm-base-entity.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Column, Entity, OneToMany } from 'typeorm';
import { DomainHoursPerWeek } from './domain-hours-per-week.entity';
import { DomainSettings } from './domain-settings.entity';
import { EnaioCertificatesTools } from './enaio-certificates-tools';
import { ExamType } from './exam-type.entity';
import { GgnWeightingSchoolCareer } from "./ggn-weighting-school-career.enum";
import { GgnWeighting } from "./ggn-weighting.entity";
import { GradeSettings } from './grade-settings.entity';
import { NumberFormatMode } from './number-format-mode.enum';
import { Pupil } from "./pupil.model";

@Entity()
export class SchoolTypeSettings extends SmBaseEntity implements CustomInitializer {
    @Column({type: "int", default: SchoolType.elementary})
    type: SchoolType = SchoolType.elementary;

    @Column({default: 0})
    year: number = 0;

    @Column({default: false})
    isHalfYear: boolean = false;

    @OneToMany(() => DomainSettings, o => o.settings, { cascade: true})
    @Type(() => DomainSettings)
    domainSettings: DomainSettings[];

    @OneToMany(() => GradeSettings, o => o.settings, { cascade: true})
    @Type(() => GradeSettings)
    gradeSettings: GradeSettings[];

    @OneToMany(() => ExamType, o => o.settings, { cascade: true})
    @Type(() => ExamType)
    examTypes: ExamType[];

    @OneToMany(() => DomainHoursPerWeek, o => o.settings, { cascade: true })
    @Type(() => DomainHoursPerWeek)
    domainHoursPerWeek: DomainHoursPerWeek[];

    @OneToMany(() => GgnWeighting, o => o.settings, { cascade: true })
    @Type(() => GgnWeighting)
    ggnWeightings: GgnWeighting[];

    @Column({type: "int", default: NumberFormatMode.decimalTwo})
    roundingType: NumberFormatMode = NumberFormatMode.decimalTwo;

    customInitializer(): void {
        this.domainSettings = Arrays.ensure(this.domainSettings);
        this.domainSettings.forEach(item => item.customInitializer());
        this.gradeSettings = Arrays.ensure(this.gradeSettings);
        this.examTypes = Arrays.ensure(this.examTypes);
        this.domainHoursPerWeek = Arrays.ensure(this.domainHoursPerWeek);
        this.ggnWeightings = Arrays.ensure(this.ggnWeightings);
    }

    formatValue(value: number, overrideRoundingType: NumberFormatMode = null): string {
        return EnaioCertificatesTools.formatValue(value, overrideRoundingType || this.roundingType);
    }

    getGradeSettings(grade: number): GradeSettings {
        this.gradeSettings = Arrays.ensure(this.gradeSettings);
        let result = this.gradeSettings.find(s => s.grade == grade);
        if (result == null) {
            result = GradeSettings.makeDefault(grade);
            this.gradeSettings.push(result);
        }
        return result;
    }

    getDomainHoursPerWeek(domain: string, grade: number): number {
        return this.domainHoursPerWeek.find(hpw => hpw.domain == domain && hpw.grade == grade)?.count ?? 0;
    }

    getGgnFactor(domain: string, grade: number, pupil: Pupil = null, ratedGgnCount: number = 0): number {
        this.ggnWeightings = Arrays.ensure(this.ggnWeightings);
        if (this.ggnWeightings.length > 0) {
            if (pupil == null) {
                return 50;
            }
            let schoolCareer = pupil.isCareerGymnasium() ? GgnWeightingSchoolCareer.gymnasium :
                pupil.isCareerRegional() || pupil.isCareerBerufsreife() ? GgnWeightingSchoolCareer.regionalOrVocational : GgnWeightingSchoolCareer.without;
            let factor = this.ggnWeightings.find(item => item.grade == grade && item.schoolCareer == schoolCareer && item.ggnCount == Math.min(3, Math.max(1, ratedGgnCount)))?.factor;
            return factor == null ? 50 : factor == 33.3 ? 100 / 3 : factor;
        }
        let pw = this.getDomainHoursPerWeek(domain, grade);
        return pw == 0 || pw == 1 ? 33.333 : pw == 2 ? 40 : 50;
    }

    getKgnFactor(domain: string, grade: number, pupil: Pupil = null, ratedGgnCount: number = 0): number {
        return 100 - this.getGgnFactor(domain, grade, pupil, ratedGgnCount);
    }

    canCopyLastYear(grade: number): boolean {
        return this.type == SchoolType.special && grade % 3 != 1;
    }
}
